import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { supabase } from '../src/lib/supabaseClient';
import { api } from '../services/apiClient';
import {
  isCreditsAccountResponseTrustworthy,
  parseRemainingCreditsFromAccountResponse,
} from '../utils/accountCreditsFromApi';

function isEmailConfirmed(user: {
  email_confirmed_at?: string | null;
  confirmed_at?: string | null;
  email_verified?: boolean;
}): boolean {
  return (
    user.email_confirmed_at != null ||
    (user as { confirmed_at?: string | null }).confirmed_at != null ||
    (user as { email_verified?: boolean }).email_verified === true
  );
}

function normalizeAccountEmail(email: string | null | undefined): string {
  return (email || '').trim().toLowerCase();
}

function useAuthEmailForAccount(): { userEmail: string | null; enabled: boolean } {
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const applySession = (session: { user?: { email?: string | null } & Record<string, unknown> } | null) => {
      const u = session?.user;
      if (!u?.email || !isEmailConfirmed(u)) {
        setUserEmail(null);
        setEnabled(false);
        return;
      }
      setUserEmail(u.email);
      setEnabled(true);
    };

    void supabase.auth.getSession().then(({ data: { session } }) => applySession(session));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[AccountProvider] ACCT_IDENTITY_TRACE', {
        event: 'auth_state_change',
        auth_event: event,
        session_email: session?.user?.email ?? null,
        auth_user_id: session?.user?.id ?? null,
      });
      applySession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { userEmail, enabled };
}

export type AccountRechargeRecord = {
  id: string;
  plan_type: string;
  payment_method?: string | null;
  display_name?: string | null;
  amount_total: number;
  amount_paid: number;
  amount_bonus: number;
  amount_remaining: number;
  purchase_price: number;
  created_at: string;
  expires_at: string;
  order_id?: string;
};

export type AccountInfo = {
  email: string;
  public_user_id: string | null;
  balance: number;
  remaining_credits: number;
  total_credits: number;
  paid_credits: number;
  bonus_credits: number;
  total_recharged: number;
  total_generated: number;
  total_points: number;
  paid_points: number;
  gift_points: number;
  remaining_points: number;
  paid_amount: number;
  image_credits: number;
  history: AccountRechargeRecord[];
};

export type AccountSyncStatus = 'idle' | 'syncing' | 'ok' | 'stale' | 'error';

export type RefreshAccountOptions = {
  silent?: boolean;
  /** 诊断日志用 */
  reason?: string;
};

type CreditsAccountApiResponse = {
  email?: string;
  public_user_id?: string | null;
  balance?: unknown;
  remaining_credits?: unknown;
  total_credits?: unknown;
  paid_credits?: unknown;
  bonus_credits?: unknown;
  total_recharged?: unknown;
  history?: Array<{
    id: string;
    plan_type?: string | null;
    payment_method?: string | null;
    display_name?: string | null;
    amount_total?: unknown;
    amount_paid?: unknown;
    amount_bonus?: unknown;
    amount_remaining?: unknown;
    purchase_price?: unknown;
    created_at?: string | null;
    expires_at?: string | null;
    order_id?: string | null;
  }>;
  diagnostics?: {
    identity_trace?: {
      request_id?: string;
      auth_user_id?: string | null;
      public_user_id?: string | null;
      ledger_query_uid?: string | null;
      ledger_page_length?: number;
      uid_drift_alert?: boolean;
    };
    [key: string]: unknown;
  };
};

export type AccountContextValue = {
  accountInfo: AccountInfo | null;
  lastKnownGoodAccount: AccountInfo | null;
  /** 无当前账号可信展示时为 null（禁止用 0 冒充「无数据」） */
  remainingCredits: number | null;
  loading: boolean;
  error: string | null;
  syncStatus: AccountSyncStatus;
  /** REST+RPC 均无法读到 ledger 且 users 有余额信号 */
  ledgerSuspiciousEmpty: boolean;
  refreshAccount: (opts?: RefreshAccountOptions) => Promise<number>;
  invalidateAccount: () => void;
};

const AccountContext = createContext<AccountContextValue | null>(null);

function mapCreditsAccountResponse(
  data: CreditsAccountApiResponse,
  fallbackEmail: string,
): AccountInfo | null {
  const remaining = parseRemainingCreditsFromAccountResponse(data);
  if (remaining === null) return null;

  const balance = Number(data.balance ?? 0);
  const totalRechargedYuan = Number(data.total_recharged || 0);
  const history = (data.history || []).map((h) => ({
    id: h.id,
    plan_type: h.plan_type || '',
    payment_method: h.payment_method,
    display_name: h.display_name,
    amount_total: Number(h.amount_total || 0),
    amount_paid: Number(h.amount_paid || 0),
    amount_bonus: Number(h.amount_bonus || 0),
    amount_remaining: Number(h.amount_remaining ?? 0),
    purchase_price: Number(h.purchase_price || 0),
    created_at: h.created_at || '',
    expires_at: h.expires_at || '',
    order_id: h.order_id || undefined,
  }));
  const ledgerPurchaseSum = history.reduce((s, h) => s + Number(h.purchase_price || 0), 0);
  const historyEarnedSum = history.reduce((s, h) => s + Number(h.amount_total || 0), 0);
  const paid_amount =
    history.length > 0
      ? ledgerPurchaseSum
      : totalRechargedYuan > 0
        ? totalRechargedYuan
        : balance;
  /** 累计获得：有 ledger 历史时与 SUM(amount_total) 一致；无历史时走 API fallback */
  const total_points =
    history.length > 0
      ? historyEarnedSum
      : Math.floor(Number(data.total_credits ?? 0));
  const totalGenerated = Math.max(0, Math.floor(totalRechargedYuan - balance));

  return {
    email: data.email || fallbackEmail,
    public_user_id: data.public_user_id ?? null,
    balance,
    remaining_credits: remaining,
    total_credits: Number(data.total_credits ?? 0),
    paid_credits: Number(data.paid_credits ?? 0),
    bonus_credits: Number(data.bonus_credits ?? 0),
    total_recharged: totalRechargedYuan,
    total_generated: totalGenerated,
    total_points,
    paid_points: Number(data.paid_credits ?? 0),
    gift_points: Number(data.bonus_credits ?? 0),
    remaining_points: remaining,
    paid_amount,
    image_credits: remaining,
    history,
  };
}

type AccountProviderProps = {
  children: React.ReactNode;
};

export function AccountProvider({ children }: AccountProviderProps) {
  const { userEmail, enabled } = useAuthEmailForAccount();
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [lastKnownGoodAccount, setLastKnownGoodAccount] = useState<AccountInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<AccountSyncStatus>('idle');
  const [ledgerSuspiciousEmpty, setLedgerSuspiciousEmpty] = useState(false);
  const fetchSeqRef = useRef(0);
  const activeEmailRef = useRef<string | null>(null);
  const lastKnownGoodByEmailRef = useRef<Record<string, AccountInfo>>({});
  const refreshInFlightRef = useRef(false);
  const loadingRef = useRef(false);

  const getLkgForEmail = useCallback((emailNorm: string): AccountInfo | null => {
    if (!emailNorm) return null;
    return lastKnownGoodByEmailRef.current[emailNorm] ?? null;
  }, []);

  const setLkgForEmail = useCallback((emailNorm: string, info: AccountInfo) => {
    lastKnownGoodByEmailRef.current[emailNorm] = info;
    if (activeEmailRef.current === emailNorm) {
      setLastKnownGoodAccount(info);
    }
  }, []);

  const applyDisplayFromAccount = useCallback(
    (info: AccountInfo | null, emailNorm: string) => {
      if (emailNorm && activeEmailRef.current !== emailNorm) return;
      setAccountInfo(info);
    },
    [],
  );

  const invalidateAccount = useCallback(() => {
    setError(null);
    setSyncStatus((s) => (s === 'ok' ? 'stale' : s));
  }, []);

  const refreshAccount = useCallback(
    async (opts?: RefreshAccountOptions): Promise<number> => {
      const emailNorm = normalizeAccountEmail(userEmail);
      const silent = opts?.silent === true;
      const reason = opts?.reason || (silent ? 'silent' : 'manual');

      if (!enabled || !emailNorm) {
        const cached = getLkgForEmail(activeEmailRef.current || '');
        return cached?.remaining_credits ?? 0;
      }

      if (silent && refreshInFlightRef.current) {
        console.log('[AccountProvider] refresh skip already in flight', {
          reason,
          silent,
          userEmail: emailNorm,
        });
        const cached = getLkgForEmail(emailNorm);
        return cached?.remaining_credits ?? 0;
      }
      if (!silent && loadingRef.current) {
        console.log('[AccountProvider] refresh skip already loading', {
          reason,
          silent,
          userEmail: emailNorm,
        });
        const cached = getLkgForEmail(emailNorm);
        return cached?.remaining_credits ?? 0;
      }

      const seq = ++fetchSeqRef.current;
      refreshInFlightRef.current = true;

      const { data: sessionData } = await supabase.auth.getSession();
      const sessionAuthUserId = sessionData.session?.user?.id ?? null;
      const sessionEmail = sessionData.session?.user?.email ?? null;
      const sessionEmailNorm = normalizeAccountEmail(sessionEmail);

      console.log('[AccountProvider] ACCT_IDENTITY_TRACE', {
        event: 'refresh_start',
        reason,
        seq,
        silent,
        userEmail: emailNorm,
        activeEmailRef: activeEmailRef.current,
        session_auth_user_id: sessionAuthUserId,
        session_email_normalized: sessionEmailNorm,
        email_session_vs_hook_match: sessionEmailNorm === emailNorm,
      });

      if (!silent) {
        loadingRef.current = true;
        setLoading(true);
      }
      if (activeEmailRef.current === emailNorm) {
        setSyncStatus('syncing');
      }

      try {
        const data = (await api.getCreditsAccount()) as CreditsAccountApiResponse;
        if (seq !== fetchSeqRef.current || activeEmailRef.current !== emailNorm) {
          console.warn('[AccountProvider] ACCT_IDENTITY_TRACE', {
            event: 'refresh_stale_discarded',
            seq,
            fetchSeqRef: fetchSeqRef.current,
            userEmail: emailNorm,
            activeEmailRef: activeEmailRef.current,
            response_public_user_id: data.public_user_id ?? null,
            response_remaining_credits: data.remaining_credits,
          });
          const cached = getLkgForEmail(emailNorm);
          return cached?.remaining_credits ?? 0;
        }

        const trace = data.diagnostics?.identity_trace as
          | {
              request_id?: string;
              auth_user_id?: string | null;
              public_user_id?: string | null;
              ledger_query_uid?: string | null;
              ledger_page_length?: number;
              remaining_credits?: number;
              uid_drift_alert?: boolean;
            }
          | undefined;
        const clientUidDrift =
          sessionAuthUserId != null &&
          trace?.auth_user_id != null &&
          sessionAuthUserId !== trace.auth_user_id;
        const clientPublicDrift =
          trace?.public_user_id != null &&
          trace.public_user_id !== trace.ledger_query_uid;
        console.log('[AccountProvider] ACCT_IDENTITY_TRACE', {
          event: 'refresh_response',
          seq,
          userEmail: emailNorm,
          session_auth_user_id: sessionAuthUserId,
          response_public_user_id: data.public_user_id ?? null,
          response_remaining_credits: data.remaining_credits,
          server_request_id: trace?.request_id ?? null,
          server_auth_user_id: trace?.auth_user_id ?? null,
          server_ledger_query_uid: trace?.ledger_query_uid ?? null,
          server_ledger_page_length: trace?.ledger_page_length,
          server_uid_drift_alert: trace?.uid_drift_alert ?? null,
          client_auth_vs_server_auth_mismatch: clientUidDrift,
          client_public_vs_ledger_uid_mismatch: clientPublicDrift,
        });

        if (!isCreditsAccountResponseTrustworthy(data)) {
          console.warn('[AccountProvider] refresh stale untrustworthy response', {
            reason,
            seq,
            silent,
            userEmail: emailNorm,
            public_user_id: data.public_user_id,
            remaining_credits: data.remaining_credits,
          });
          setLedgerSuspiciousEmpty(false);
          setError('账户同步异常：服务端返回无效余额，已保留上次成功结果');
          const lkg = getLkgForEmail(emailNorm);
          if (lkg) {
            applyDisplayFromAccount(lkg, emailNorm);
            setSyncStatus('stale');
          } else if (activeEmailRef.current === emailNorm) {
            setSyncStatus('error');
          }
          return lkg?.remaining_credits ?? 0;
        }

        const ledgerSyncFailed = data.diagnostics?.ledger_suspicious_empty === true;
        if (ledgerSyncFailed) {
          console.warn('[AccountProvider] ledger_suspicious_empty', {
            reason,
            seq,
            userEmail: emailNorm,
            ledger_read_source: data.diagnostics?.ledger_read_source ?? null,
            remaining_credits: data.remaining_credits,
          });
          setLedgerSuspiciousEmpty(true);
          setError('账户记录同步异常，请刷新或联系客服');
          const lkg = getLkgForEmail(emailNorm);
          if (lkg) {
            applyDisplayFromAccount(lkg, emailNorm);
            setSyncStatus('stale');
          } else {
            const mappedPartial = mapCreditsAccountResponse(data, emailNorm);
            if (mappedPartial) {
              applyDisplayFromAccount(mappedPartial, emailNorm);
            }
            if (activeEmailRef.current === emailNorm) {
              setSyncStatus('error');
            }
          }
          const remaining = parseRemainingCreditsFromAccountResponse(data);
          return remaining ?? lkg?.remaining_credits ?? 0;
        }

        const mapped = mapCreditsAccountResponse(data, emailNorm);
        if (!mapped) {
          console.warn('[AccountProvider] refresh stale parse failed', { reason, seq, silent, userEmail: emailNorm });
          setError('账户同步异常：无法解析 remaining_credits');
          const lkg = getLkgForEmail(emailNorm);
          if (lkg) {
            applyDisplayFromAccount(lkg, emailNorm);
            setSyncStatus('stale');
          } else if (activeEmailRef.current === emailNorm) {
            setSyncStatus('error');
          }
          return lkg?.remaining_credits ?? 0;
        }

        setLkgForEmail(emailNorm, mapped);
        applyDisplayFromAccount(mapped, emailNorm);
        setLedgerSuspiciousEmpty(false);
        setError(null);
        if (activeEmailRef.current === emailNorm) {
          setSyncStatus('ok');
        }
        console.log('[AccountProvider] refresh success', {
          reason,
          seq,
          silent,
          userEmail: emailNorm,
          remaining_credits: mapped.remaining_credits,
        });
        return mapped.remaining_credits;
      } catch (e: unknown) {
        if (seq !== fetchSeqRef.current || activeEmailRef.current !== emailNorm) {
          const cached = getLkgForEmail(emailNorm);
          return cached?.remaining_credits ?? 0;
        }
        const msg = e instanceof Error ? e.message : String(e);
        console.warn('[AccountProvider] refresh fail', { reason, seq, silent, userEmail: emailNorm, message: msg });
        setError(msg);
        const lkg = getLkgForEmail(emailNorm);
        if (lkg) {
          applyDisplayFromAccount(lkg, emailNorm);
          setSyncStatus('stale');
        } else if (activeEmailRef.current === emailNorm) {
          setSyncStatus('error');
        }
        return lkg?.remaining_credits ?? 0;
      } finally {
        refreshInFlightRef.current = false;
        if (seq === fetchSeqRef.current && activeEmailRef.current === emailNorm && !silent) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [enabled, userEmail, applyDisplayFromAccount, getLkgForEmail, setLkgForEmail],
  );

  const refreshAccountRef = useRef(refreshAccount);
  refreshAccountRef.current = refreshAccount;

  useEffect(() => {
    const emailNorm = normalizeAccountEmail(userEmail);
    if (!enabled || !emailNorm) {
      fetchSeqRef.current += 1;
      refreshInFlightRef.current = false;
      loadingRef.current = false;
      setLoading(false);
      setError(null);
      setLedgerSuspiciousEmpty(false);
      setSyncStatus('idle');
      setAccountInfo(null);
      setLastKnownGoodAccount(null);
      activeEmailRef.current = null;
      return;
    }

    const prevEmail = activeEmailRef.current;
    if (prevEmail && prevEmail !== emailNorm) {
      fetchSeqRef.current += 1;
      console.log('[AccountProvider] ACCT_IDENTITY_TRACE', {
        event: 'email_switch',
        prevEmail,
        nextEmail: emailNorm,
        fetchSeqRef: fetchSeqRef.current,
      });
      setAccountInfo(null);
    }

    activeEmailRef.current = emailNorm;
    const cached = getLkgForEmail(emailNorm);
    setLastKnownGoodAccount(cached);
    setSyncStatus(cached ? 'stale' : 'syncing');
    setLedgerSuspiciousEmpty(false);
    setError(null);

    void refreshAccountRef.current({ silent: true, reason: 'login_or_email_ready' });
  }, [enabled, userEmail, getLkgForEmail]);

  const emailNorm = normalizeAccountEmail(userEmail);
  const displayAccount =
    accountInfo ?? (emailNorm ? getLkgForEmail(emailNorm) : null) ?? null;
  const remainingCredits: number | null =
    displayAccount != null ? displayAccount.remaining_credits : null;

  const value = useMemo<AccountContextValue>(
    () => ({
      accountInfo: displayAccount,
      lastKnownGoodAccount: emailNorm ? getLkgForEmail(emailNorm) : null,
      remainingCredits,
      loading,
      error,
      syncStatus,
      ledgerSuspiciousEmpty,
      refreshAccount,
      invalidateAccount,
    }),
    [
      displayAccount,
      emailNorm,
      getLkgForEmail,
      remainingCredits,
      loading,
      error,
      syncStatus,
      ledgerSuspiciousEmpty,
      refreshAccount,
      invalidateAccount,
    ],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountContextValue {
  const ctx = useContext(AccountContext);
  if (!ctx) {
    throw new Error('useAccount must be used within AccountProvider');
  }
  return ctx;
}

export default AccountContext;
